import "service/errors"
import type { AppProps } from "@blitzjs/next"
import { init } from "@sentry/react"
import { getAnalytics, setAnalyticsCollectionEnabled } from "firebase/analytics"
import { getApps, initializeApp } from "firebase/app"
import { connectAuthEmulator, getAuth } from "firebase/auth"
import i18next from "i18next"
import type { FC } from "react"
import { useEffect } from "react"
import { AppContextProvider } from "app/components/AppContextProvider"
import { DevModeToggle } from "app/components/dev"
import { DevModeProvider } from "app/contexts/DevModeContext"
import i18nJson from "i18n.json"
import { Config } from "interface/config"
import {
  isUnattributableThirdPartyEvent,
  sentryIgnoreErrors,
} from "lib/sentryFilters"
import RootLayout from "pages/layout"
import { ErrorProvider } from "providers/ErrorProvider"
import { PostHogPageview, PostHogProvider } from "providers/PostHogProvider"

// 遅延読み込みヘルパー
const loadWhenIdle = (fn: () => void, fallbackDelay = 2000) => {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(fn)
  } else {
    setTimeout(fn, fallbackDelay)
  }
}

export const MyApp: FC<AppProps> = ({ Component, pageProps, router }) => {
  const getLayout = Component.getLayout ?? ((page) => page)

  // 言語はURLロケールに追従させる(#925)。従来はインスタンス設定で固定され、
  // /ja でも英語ラベルが表示されていた。locale未指定時は従来の既定を維持。
  useEffect(() => {
    const locale = router.locale ?? (Config.isInstanceSushiTrain ? "en" : "ja")
    if (i18next.language !== locale) {
      void i18next.changeLanguage(locale)
    }
  }, [router.locale])

  // Crisp - 遅延読み込み
  useEffect(() => {
    loadWhenIdle(async () => {
      const { Crisp } = await import("crisp-sdk-web")
      Crisp.configure(Config.crispWebsiteId)
    })
  }, [])

  return (
    <PostHogProvider>
      <PostHogPageview />
      <DevModeProvider>
        <AppContextProvider>
          <RootLayout>
            <ErrorProvider>
              {getLayout(<Component {...pageProps} />)}
            </ErrorProvider>
          </RootLayout>
        </AppContextProvider>
        <DevModeToggle />
      </DevModeProvider>
    </PostHogProvider>
  )
}

/**
 * Sentry (エラートラッキングのみ、Replay は PostHog で代替)
 */
if (typeof window !== "undefined") {
  // クライアント初回ロード時刻とバージョンを記録
  if (!sessionStorage.getItem("app_loaded_at")) {
    sessionStorage.setItem("app_loaded_at", new Date().toISOString())
    sessionStorage.setItem("app_version", Config.sentryRelease || "")
  }

  init({
    dsn: Config.sentryDSN,
    environment: Config.sentryEnvironment,
    tracesSampleRate: 1.0,
    attachStacktrace: true,
    normalizeDepth: 5,
    release: Config.sentryRelease,
    // ブラウザ/通信環境起因のノイズを除外（#864）。パターンと安全性の根拠は
    // lib/sentryFilters.ts を参照。
    ignoreErrors: sentryIgnoreErrors,

    beforeSend(event) {
      // ブラウザ注入スクリプト由来で発生箇所を特定できないノイズを破棄（#862）。
      // 判定と安全性の根拠は lib/sentryFilters.ts を参照。
      if (isUnattributableThirdPartyEvent(event)) {
        return null
      }

      // ビルドバージョンを常に付与
      event.tags = {
        ...event.tags,
        clientBuildVersion: Config.sentryRelease,
        appLoadedAt: sessionStorage.getItem("app_loaded_at") || undefined,
      }

      // キャッシュ関連エラーを自動検知
      const errorType = event.exception?.values?.[0]?.type
      const errorMessage = event.exception?.values?.[0]?.value || ""

      const cacheRelatedPatterns = [
        "ChunkLoadError",
        "Loading chunk",
        "is not a function",
        "is not defined",
        "Failed to fetch dynamically imported module",
      ]

      const isPossibleCacheIssue =
        errorType === "ChunkLoadError" ||
        cacheRelatedPatterns.some((pattern) => errorMessage.includes(pattern))

      if (isPossibleCacheIssue) {
        event.tags = {
          ...event.tags,
          possibleCacheIssue: "true",
          cacheErrorType: errorType || "unknown",
        }
      }

      return event
    },
  })
}

/**
 * Firebase
 */
if (Config.isClient && getApps().length === 0) {
  initializeApp(Config.firebaseOptions)
  if (Config.isNotProduction) {
    setAnalyticsCollectionEnabled(getAnalytics(), false)
  }
  // Connect Firebase Auth to emulator when running locally
  const emulatorHost = process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST
  if (emulatorHost) {
    connectAuthEmulator(getAuth(), `http://${emulatorHost}`, {
      disableWarnings: true,
    })
  }
}

i18next.init({
  lng: Config.isInstanceSushiTrain ? "en" : "ja",
  fallbackLng: "en",
  resources: {
    en: {
      translation: Object.fromEntries(
        Object.keys(i18nJson).map((key) => {
          return [key, key]
        }),
      ),
    },
    ja: {
      translation: Object.fromEntries(
        Object.keys(i18nJson).map((key) => {
          return [key, i18nJson[key as keyof typeof i18nJson]]
        }),
      ),
    },
  },
})

export default MyApp
